import {
	faCheck,
	faChevronDown,
	faSpinner,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, Transition } from '@headlessui/react';
import React, { Fragment, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
	getKeyboardMenuMaxHeight,
	useKeyboardAwareFloating,
} from '../../hooks/useKeyboardAwareFloating';
import { useLayerZIndex } from '../../providers/LayerProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { resolveAutofillOption } from '../../utils/select-autofill';

interface SelectOption {
	value: string | number;
	label: string;
	disabled?: boolean;
	/**
	 * PD-15878 — a mark rendered before the label, in the closed control as
	 * well as in the menu. Added for payment methods, where a card's network
	 * is recognized from its logo long before the four digits are read;
	 * anything that identifies an option at a glance belongs here.
	 *
	 * Optional and inert when absent, so every existing caller is unchanged.
	 */
	icon?: React.ReactNode;
	/**
	 * PD-16381 — a second, smaller line under the label in the open menu, for
	 * the details that help choose between options but crowd the label when
	 * run onto it (a payment method's funding type, fee and account). Same
	 * name and role as `SelectSearchOption.subtitle`. Shown in the menu only;
	 * the closed control keeps its single-line height. Optional and inert when
	 * absent.
	 */
	subtitle?: string;
	/**
	 * PD-16381 — short text kept visible after the label in the CLOSED control
	 * once this option is chosen (a payment method's fee). The label truncates
	 * before it does, so it survives a narrow control. Optional and inert when
	 * absent.
	 */
	selectedSuffix?: string;
	/**
	 * PD-17144 — a heading this option sits under in the open menu, the
	 * Listbox equivalent of a native `<optgroup>`. A heading is drawn before
	 * the first option of each run of consecutive options sharing a group, so
	 * callers pass options already ordered by group. Options without a group
	 * render as before. Optional and inert when absent.
	 */
	group?: string;
}

// `size` is omitted along with `onChange` — this component repurposes
// `size` as a design-size token, not the native row count.
interface SelectProps
	extends Omit<
		React.SelectHTMLAttributes<HTMLSelectElement>,
		'onChange' | 'size'
	> {
	options: SelectOption[];
	placeholder?: string;
	error?: boolean;
	fullWidth?: boolean;
	onChange?: (value: string) => void;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	allowClear?: boolean;
	onOpen?: () => void;
	isLoading?: boolean;
	loadingText?: string;
	/**
	 * PD-16249 — a WHATWG autofill token (`address-level1`, `country`, …).
	 * Setting it renders a hidden native input the browser can fill; the text
	 * it fills is matched onto an option by value or label. Left unset, the
	 * control is a plain Listbox with nothing for autofill to target.
	 */
	autoComplete?: string;
}

export function Select({
	options,
	placeholder,
	error = false,
	fullWidth = true,
	onChange,
	disabled,
	className,
	value,
	size = 'md',
	allowClear = false,
	onOpen,
	isLoading = false,
	loadingText = 'Loading...',
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledBy,
	required = false,
	name,
	autoComplete,
}: SelectProps) {
	const colors = useThemeColors();
	const wasOpenRef = useRef(false);
	// The menu portals to <body>, so it competes with every other overlay at
	// the top of the page. 1000 clears a Modal or Sidebar; inside a Popover
	// (3000) or any other overlay above that, the menu is lifted above the
	// overlay it was opened from instead of opening underneath it (PD-16154).
	const menuZIndex = useLayerZIndex(1000);

	// Floating UI to render the menu in a portal at body-level so it
	// escapes any ancestor `overflow-auto` (sidebars, scroll containers)
	// that would otherwise clip the dropdown. Mirrors the SelectSearch
	// pattern used elsewhere in this codebase.
	//
	// PD-16133 — the menu also measures itself rather than inheriting the
	// control's width. A narrow control (a duration picker in a row of small
	// controls, a toolbar select) produced a menu as narrow as itself, in which
	// every option was truncated to a fragment — "15 min" read as "1…". The
	// menu is now at least as wide as the control and grows to its widest
	// option, capped to what the viewport can show, which is what a native
	// select does. `size` also caps the height to the space available, so a
	// long list near the foot of the window scrolls instead of running off it.
	//
	// PD-14097 — the shared hook keeps the menu above the native app's
	// keyboard. While the keyboard is up the 120px floor gives way to the
	// room actually left above it, so the options are not hidden behind it.
	const { x, y, strategy, refs } = useKeyboardAwareFloating({
		applySize({
			rects,
			availableWidth,
			availableHeight,
			elements,
			keyboardHeight,
		}) {
			const maxHeight =
				keyboardHeight > 0
					? getKeyboardMenuMaxHeight(availableHeight, 240)
					: Math.max(120, Math.min(240, availableHeight));
			Object.assign(elements.floating.style, {
				minWidth: `${String(rects.reference.width)}px`,
				maxWidth: `${String(Math.max(rects.reference.width, availableWidth))}px`,
				maxHeight: `${String(maxHeight)}px`,
			});
		},
	});

	// Find the selected option
	const selectedOption = options?.find((opt) => opt.value === value);

	// Size-based styling to match MultiSelect
	const sizeClasses = {
		xs: 'px-2.5 py-1 text-xs h-[var(--control-h-xs,2rem)]',
		sm: 'px-3 py-1.5 text-sm min-h-[30px]',
		md: 'px-3 py-2 text-sm h-[var(--control-h,2.25rem)]',
		lg: 'px-4 py-2.5 text-base min-h-[42px]',
	}[size];

	// Size-based styling for dropdown options
	const optionSizeClasses = {
		xs: 'py-1 pl-2.5 pr-7 text-xs',
		sm: 'py-1.5 pl-3 pr-7 text-sm',
		md: 'py-1.5 pl-3 pr-7 text-sm',
		lg: 'py-2 pl-4 pr-8 text-base',
	}[size];

	const handleChange = useCallback(
		(optionValue: string | number) => {
			onChange?.(String(optionValue));
		},
		[onChange],
	);

	const handleClear = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onChange?.('');
		},
		[onChange],
	);

	// PD-16249 — the browser hands over free text (a saved state as "CA" or
	// "California", in any casing); select the option it names, or leave the
	// control alone when nothing matches.
	const handleAutofill = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// The browser hands over free text — a saved state arrives as "MI"
			// or as "Michigan", in any casing — so it is matched onto an option
			// by value or by label, and ignored when it matches neither.
			const match = resolveAutofillOption(options, event.target.value);
			if (match != null) {
				onChange?.(String(match.value));
			}
		},
		[onChange, options],
	);

	const hasValue = value != null && value !== '';
	const showClearButton = allowClear && hasValue && !disabled;

	// PD-16133 — the room kept on the right for the chevron (and the clear
	// mark). The chevron needs 20px; `pr-10` reserved 40px of a control only
	// 60px wide, so an xs control clipped its own value ("30 min" read as
	// "30 mi"). The larger sizes keep the roomier padding they had.
	const trailingPadding =
		size === 'xs'
			? showClearButton
				? 'pr-12'
				: 'pr-7'
			: showClearButton
				? 'pr-16'
				: 'pr-10';

	return (
		<Listbox value={value ?? ''} onChange={handleChange} disabled={disabled}>
			{({ open }) => {
				// Call onOpen when dropdown opens
				if (open && !wasOpenRef.current && onOpen != null) {
					// Use setTimeout to avoid calling during render
					setTimeout(() => {
						onOpen();
					}, 0);
				}
				wasOpenRef.current = open;

				return (
					<div className={cn('relative', fullWidth ? 'w-full' : 'w-auto')}>
						<Listbox.Button
							ref={refs.setReference}
							// Toolbar selects often have no visible label; forward the
							// aria naming so assistive technology and tests can find them.
							aria-label={ariaLabel}
							aria-required={required || undefined}
							aria-labelledby={ariaLabelledBy}
							className={cn(
								// Base styling
								'relative w-full cursor-pointer text-left',

								// Size-based padding and text
								sizeClasses,

								// The room kept on the right for the chevron. It follows
								// the size classes: `cn` merges conflicting Tailwind
								// padding utilities by keeping the last one, and `px-*`
								// listed after it used to cancel it, which is why the
								// chevron sat on top of the value (PD-16133).
								trailingPadding,

								// Use form theme classes
								colors.classes.formFieldBase,

								// State-based styling
								error
									? colors.classes.formFieldError
									: colors.classes.formFieldDefault,

								// Focus state
								colors.classes.formFieldFocus,

								disabled && colors.classes.formFieldDisabled,

								fullWidth && 'w-full',
								className,
							)}
						>
							<span className="flex items-center gap-2 min-w-0">
								{selectedOption?.icon != null && (
									<span className="shrink-0 inline-flex items-center leading-none">
										{selectedOption.icon}
									</span>
								)}
								<span
									className={cn(
										'block truncate',
										// PD-16249 — the same tone a native input paints its
										// placeholder in, so a dropdown and the fields beside
										// it read as one set of controls.
										!selectedOption && placeholder && 'text-subtle',
									)}
								>
									{selectedOption
										? selectedOption.label
										: placeholder || 'Select...'}
								</span>
								{selectedOption?.selectedSuffix != null &&
									selectedOption.selectedSuffix !== '' && (
										<span
											className={cn(
												'shrink-0 whitespace-nowrap text-xs',
												colors.classes.textSecondary,
											)}
										>
											{selectedOption.selectedSuffix}
										</span>
									)}
							</span>

							{/* Clear button — matches BaseTextInput's clear: just a
							    color transition on the icon, no background. */}
							{showClearButton && (
								<div
									role="button"
									tabIndex={0}
									onClick={handleClear}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleClear(e as any);
										}
									}}
									className={cn(
										'absolute inset-y-0 right-8 flex items-center justify-center w-6 cursor-pointer',
									)}
								>
									<FontAwesomeIcon
										icon={faXmark}
										className={cn(
											'h-3 w-3',
											colors.classes.textSecondary,
											'hover:' + colors.classes.textPrimary,
											'transition-colors',
										)}
										aria-hidden="true"
									/>
								</div>
							)}

							{/* Dropdown chevron */}
							<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
								<FontAwesomeIcon
									icon={faChevronDown}
									// PD-16249 — the quiet tone the placeholder uses, so the
									// affordance does not read louder than the value itself.
									className={cn('h-3 w-3 text-subtle')}
									aria-hidden="true"
								/>
							</span>
						</Listbox.Button>

						{autoComplete != null && (
							// PD-16249 — browser autofill writes only into native form
							// controls, and a Listbox is a button plus a menu, so a state
							// dropdown never received the saved address that filled the
							// inputs around it. This input exists purely as the browser's
							// fill target; whatever text it receives is matched onto an
							// option by `handleAutofill`.
							//
							// A native select was tried first, on the reasoning that a
							// browser would match a saved "MI" or "Michigan" against the
							// options itself. It fills either shape equally well, but a
							// select is painted differently: the pale wash a browser puts
							// on a field it is previewing or has filled is drawn outside
							// normal element rendering, so neither layering nor a filter
							// could keep it off the control. On an input that wash is an
							// ordinary background and disappears with the rest of it.
							//
							// It sits behind the button, which paints an opaque surface of
							// its own, and paints nothing itself. The element keeps the
							// control's full size and its `opacity` and `visibility`
							// untouched, because those are what a browser reads when it
							// decides whether a field can be filled at all; a filter is
							// applied later, when the pixels are drawn. It stays out of the
							// tab order, the accessibility tree and pointer events, so the
							// Listbox button remains the control the user operates.
							<input
								// Marks this as the browser's fill target rather than a
								// control of its own, so an app's global styling for native
								// fields — including the rules that repaint a filled one —
								// leaves it alone. It must paint nothing in any state.
								data-autofill-proxy="true"
								type="text"
								tabIndex={-1}
								aria-hidden="true"
								name={name}
								autoComplete={autoComplete}
								disabled={disabled}
								value={
									selectedOption != null ? String(selectedOption.value) : ''
								}
								onChange={handleAutofill}
								className="absolute inset-0 h-full w-full pointer-events-none"
								// Written inline because every app styles bare fields
								// globally — a border, a surface background, padding and a
								// shadow — and an element rule outranks a utility class, so
								// the overlay painted itself over the button and hid its
								// label and chevron. An inline style beats an element rule
								// wherever this renders.
								style={{
									zIndex: -1,
									filter: 'opacity(0)',
									appearance: 'none',
									background: 'transparent',
									color: 'transparent',
									// What actually paints the glyphs. A browser marks a
									// field it filled with its own `!important` text color,
									// which outranks `color`; nothing sets this.
									WebkitTextFillColor: 'transparent',
									caretColor: 'transparent',
									border: 0,
									borderRadius: 0,
									boxShadow: 'none',
									outline: 'none',
									padding: 0,
									margin: 0,
								}}
							/>
						)}

						{createPortal(
							<Transition
								as={Fragment}
								show={open}
								leave="transition ease-in duration-100"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<Listbox.Options
									ref={refs.setFloating}
									static
									// PD-15878 — a dropdown is not a modal. Headless UI v2 makes an
									// open listbox modal by default: it locks scrolling on <html> and
									// marks everything outside the list inert. Inside the CRM that is
									// invisible because the pages scroll in an inner container, but
									// on a body-scrolled page (the storefront) the lock re-homes the
									// scroll to <body>, which blanks the viewport behind the list,
									// pads <html> by a scrollbar width so the page shifts, and drops
									// the scroll position when the list closes. A native <select>
									// does none of that, and neither should this.
									modal={false}
									style={{
										position: strategy,
										top: y ?? 0,
										left: x ?? 0,
										// The control's width is the floor, not the ceiling:
										// `size` above sets minWidth from the control and the
										// caps, and the list takes the width its options need
										// (PD-16133).
										width: 'max-content',
										zIndex: menuZIndex,
									}}
									className={cn(
										'overflow-auto custom-scrollbar rounded-md py-1 text-base sm:text-sm',
										colors.classes.selectMenu,
									)}
								>
									{isLoading ? (
										<div
											className={cn(
												'relative cursor-default py-2 px-3 text-center',
												colors.classes.textSecondary,
											)}
										>
											<FontAwesomeIcon
												icon={faSpinner}
												className="animate-spin mr-2"
											/>
											{loadingText}
										</div>
									) : (
										<>
											{/* A required field shows its placeholder only as the
											    empty state: offering it as a choice would let the
											    user "pick" nothing. */}
											{placeholder != null && !required && (
												<Listbox.Option
													key="__placeholder__"
													value=""
													className={({ active }) =>
														cn(
															'relative cursor-pointer transition-colors',
															optionSizeClasses,
															active && colors.classes.selectOption,
															!active && colors.classes.textSecondary,
														)
													}
												>
													{placeholder}
												</Listbox.Option>
											)}
											{(options ?? []).map((option, index) => (
												<React.Fragment key={option.value}>
												{option.group != null &&
													option.group !== '' &&
													option.group !== options[index - 1]?.group && (
														// PD-17144 — a heading, not an option: Listbox skips
														// it for keyboard navigation and selection.
														<li
															role="presentation"
															className={cn(
																'list-none px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide cursor-default select-none',
																colors.classes.textSecondary,
															)}
														>
															{option.group}
														</li>
													)}
												<Listbox.Option
													value={option.value}
													disabled={option.disabled}
													className={({ active, selected }) =>
														cn(
															'relative transition-colors',
															optionSizeClasses,
															option.disabled
																? 'cursor-not-allowed opacity-50'
																: 'cursor-pointer',
															selected &&
																!active &&
																colors.classes.selectOptionSelected,
															active && colors.classes.selectOption,
															!selected &&
																!active &&
																colors.classes.textPrimary,
														)
													}
												>
													{({ selected }) => (
														<>
															<span className="flex items-center gap-2 min-w-0">
																{option.icon != null && (
																	<span className="shrink-0 inline-flex items-center leading-none">
																		{option.icon}
																	</span>
																)}
																{option.subtitle != null &&
																option.subtitle !== '' ? (
																	<span className="flex min-w-0 flex-col">
																		<span
																			className={cn(
																				'block truncate',
																				selected
																					? 'font-medium'
																					: 'font-normal',
																			)}
																		>
																			{option.label}
																		</span>
																		<span
																			className={cn(
																				'block truncate text-xs',
																				colors.classes.textSecondary,
																			)}
																		>
																			{option.subtitle}
																		</span>
																	</span>
																) : (
																	<span
																		className={cn(
																			'block truncate',
																			selected ? 'font-medium' : 'font-normal',
																		)}
																	>
																		{option.label}
																	</span>
																)}
															</span>
															{selected && (
																<span
																	className={cn(
																		'absolute inset-y-0 right-0 flex items-center pr-1.5',
																		colors.classes.textPrimary,
																	)}
																>
																	<FontAwesomeIcon
																		icon={faCheck}
																		className="h-3 w-3"
																		aria-hidden="true"
																	/>
																</span>
															)}
														</>
													)}
												</Listbox.Option>
												</React.Fragment>
											))}
										</>
									)}
								</Listbox.Options>
							</Transition>,
							document.body,
						)}
					</div>
				);
			}}
		</Listbox>
	);
}
